import { makeStyles } from "@mui/styles";
import { useCallback, useState } from "react";
import { ErrorCode, useDropzone } from "react-dropzone";

import { ELEMENT_FORM_STATE } from "@/constants/index.js";

import { ERROR_MESSAGES } from "./ERROR_MESSAGES.js";
import AudioPreview from "../AudioPreview/AudioPreview";

const BYTES_PER_KB = 1024;
const KB_PER_MB = 1024;
const MAX_SIZE_MB = 200;

const useStyles = makeStyles({
  container: {
    borderRadius: "2px",
    border: (props) => (!props.selectedFile ? "1px dashed #858585" : "none"),
    padding: (props) => (!props.selectedFile ? "16px" : "16px 0px"),
    display: "flex",
    alignItems: "center",
    height: (props) => (!props.selectedFile ? "80px" : "40px"),
    justifyContent: (props) => (!props.selectedFile ? "center" : "space-between"),
    flexDirection: (props) => (!props.selectedFile ? "column" : "row"),
    fontSize: (props) => (!props.selectedFile ? "16px" : "12px"),
    background: (props) => (!props.isDragActive ? "transparent" : "rgba(0,0,0,.3)"),
  },
  title: {
    fontSize: "16px",
    fontWeight: 400,
  },
  description: {
    color: "#858585",
    fontSize: "12px",
    marginTop: "8px",
  },
  fileTitle: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginRight: "8px",
  },
  customFileUpload: {
    textDecoration: "underline",
    cursor: "pointer",
  },
  fileSize: {
    flexShrink: 0,
  },
  imageContainer: {
    borderRadius: "3px",
    position: "relative",
    overflow: "hidden",
    width: "100%",
  },
  progressBar: {
    position: "relative",
    height: "15px",
  },
  errorMessage: {
    color: "#E80239",
    display: "inline-block",
    fontSize: "12px",
    marginTop: "8px",
    paddingLeft: "16px",
  },
});

// interface VoiceMemoUploadBoxProps {
//   element: proptypes.object,
//   formValues: proptypes.object,
//   selectedFile: proptypes.object,
//   setSelectedFile: proptypes.func,
// };

export default function VoiceMemoUploadBox(props) {
  const {
    formValues,
    setValue,
    selectedFile,
    setSelectedFile,
    elementFormState,
    setElementFormState,
    previewUrl,
    setPreviewUrl,
    element,
  } = props;
  const [fileErrorMessage, setFileErrorMessage] = useState("");

  const isErrors = (fileRejections) => {
    setFileErrorMessage("");
    if (fileRejections.length) {
      const { errors } = fileRejections[0];

      let errorMessage =
        errors[0].code === ErrorCode.FileInvalidType ? ERROR_MESSAGES["file-invalid-type"] : errors[0].message;
      errorMessage = errors[0].code === ErrorCode.FileTooLarge ? ERROR_MESSAGES["file-too-large"] : errorMessage;

      setFileErrorMessage(errorMessage);
      return true;
    }
    return false;
  };

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (isErrors(fileRejections)) return;

    const file = acceptedFiles[0];

    setSelectedFile(file);

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setValue("url", url, { shouldDirty: true });
    setElementFormState(ELEMENT_FORM_STATE.PREVIEW);
  }, []);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      "audio/*": [".mp3", ".aac", ".wav", ".wma"],
    },
    maxSize: MAX_SIZE_MB * BYTES_PER_KB * KB_PER_MB,
    noClick: true,
    maxFiles: 1,
  });

  const classes = useStyles({ selectedFile, isDragActive });

  const shouldDisplayDropzone = !selectedFile && !formValues.url;

  return (
    <>
      <div data-testid="dropzone" style={{ display: shouldDisplayDropzone ? "block" : "none" }}>
        <div className={classes.container} {...getRootProps()}>
          <input {...getInputProps()} data-testid="dropzone-input" />
          <span className={classes.title}>
            Drag and drop or{" "}
            <span
              className={classes.customFileUpload}
              onClick={open}
              onKeyDown={open}
              data-analytics={`${element.type}-upload-browse`}
            >
              browse
            </span>
          </span>
          <span className={classes.description}>MP3, ACC, WAV, WMA (Max: 200 MB)</span>
        </div>
        {fileErrorMessage.length > 0 && <span className={classes.errorMessage}> {fileErrorMessage}</span>}
      </div>
      <AudioPreview elementFormState={elementFormState} previewUrl={previewUrl} replace={open} type={element.type} />
    </>
  );
}
